/**
 * Visible online channels start together. Front and rear do not wait on each
 * other, and a camera that is still opening does not take a slot from the rest
 * of the page. A channel that is already publishing is not commanded again.
 */

/** New 0x9101 posts in one pass. A full wall page fits; only this same channel's in-flight HTTP is skipped. */
export const LIVE_QUEUE_CONCURRENCY = 64;
/** Players mounted at once. Further online cameras show Click to play. */
export const LIVE_PLAY_CAP = 64;
/** Slow retry after FAILED while the heartbeat is still fresh. */
export const LIVE_HEAL_AFTER_MS = 60_000;
export const LIVE_HEAL_MAX = 3;
/** Do not send this channel another 0x9101 while its upload can still open. */
export const LIVE_QUEUE_SETTLE_MS = 90_000;
/** Rear is started with front. A dark front channel must not hold the rear channel. */
export const LIVE_SIBLING_WAIT_MS = 0;

export interface LiveQueueTarget {
  deviceId: string;
  channel: number;
  online: boolean;
  /** On-screen cards are ordered ahead of the rest. */
  visible: boolean;
  /** Click to play. Admitted even when the page is at the play cap. */
  promoted?: boolean;
}

export interface LiveQueueFact {
  live?: boolean;
  publishing?: boolean;
  /** Seen a publisher or a picture. Not commanded again. */
  pinned?: boolean;
  startedAt?: number | null;
  failed?: boolean;
  healReady?: boolean;
  inflight?: boolean;
}

export interface LiveQueuePlan {
  start: Array<{ deviceId: string; channel: number }>;
  /** 1-based position among vehicles still waiting for a slot. */
  position: Record<string, number>;
  /** Channel keys over the play cap. The card shows Click to play. */
  hold: string[];
}

function chKey(deviceId: string, channel: number): string {
  return `${deviceId}:${channel}`;
}

export function planLiveQueue(input: {
  targets: readonly LiveQueueTarget[];
  facts: ReadonlyMap<string, LiveQueueFact>;
  now: number;
  concurrency?: number;
  playCap?: number;
  siblingWaitMs?: number;
  settleMs?: number;
}): LiveQueuePlan {
  const concurrency = input.concurrency ?? LIVE_QUEUE_CONCURRENCY;
  const playCap = input.playCap ?? LIVE_PLAY_CAP;
  void input.siblingWaitMs;
  const settleMs = input.settleMs ?? LIVE_QUEUE_SETTLE_MS;
  const now = input.now;
  const facts = input.facts;

  const factOf = (target: LiveQueueTarget) => facts.get(chKey(target.deviceId, target.channel));
  const up = (target: LiveQueueTarget) => {
    const fact = factOf(target);
    return Boolean(fact?.live || fact?.publishing || fact?.pinned);
  };
  const onPage = (target: LiveQueueTarget) => target.visible || target.promoted === true;
  const needsCommand = (target: LiveQueueTarget) => {
    if (!target.online || !onPage(target) || up(target)) return false;
    const fact = factOf(target);
    if (fact?.inflight) return false;
    if (fact?.failed && !fact.healReady) return false;
    if (fact?.startedAt != null && !fact.healReady && now - fact.startedAt < settleMs) return false;
    return true;
  };

  let playing = 0;
  for (const target of input.targets) {
    if (!target.online) continue;
    const fact = factOf(target);
    if (up(target) || fact?.inflight || (fact?.startedAt != null && !fact.failed)) playing += 1;
  }

  const ranked = input.targets
    .map((target, index) => ({ target, index }))
    .filter(({ target }) => needsCommand(target))
    .sort((a, b) => {
      const rank = (target: LiveQueueTarget) => (target.promoted ? 0 : 1);
      return rank(a.target) - rank(b.target) || a.index - b.index;
    });

  const start: Array<{ deviceId: string; channel: number }> = [];
  const admitted = new Set<string>();
  // An in-flight channel is already excluded by needsCommand. It must not
  // spend a slot that another visible channel needs.
  let slots = concurrency;
  for (const { target } of ranked) {
    const key = chKey(target.deviceId, target.channel);
    if (!target.promoted && playing >= playCap) continue;
    if (slots <= 0) break;
    slots -= 1;
    playing += 1;
    admitted.add(key);
    start.push({ deviceId: target.deviceId, channel: target.channel });
  }

  const position: Record<string, number> = {};
  const hold: string[] = [];
  const waiting: string[] = [];
  const seen = new Set<string>();
  for (const { target } of ranked) {
    const key = chKey(target.deviceId, target.channel);
    if (admitted.has(key)) continue;
    if (!target.promoted && playing >= playCap) {
      hold.push(key);
      continue;
    }
    if (seen.has(target.deviceId)) continue;
    seen.add(target.deviceId);
    waiting.push(target.deviceId);
  }
  waiting.forEach((id, index) => {
    position[id] = index + 1;
  });

  return { start, position, hold };
}
